import { Telemetry, ValidTelemetryEvent } from '~commonInfra/services/Telemetry';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

/**
 * PFT (Product Feature Telemetry) events fired when a user accesses content
 * (workbook or datasource) that belongs to the Admin Insights project.
 * Mapped in Juno to:
 *   Tableau Cloud > Admin Insights > Workbook Access / Datasource Access
 *
 * Wire event names: `admin_insights_workbook` / `admin_insights_datasource`
 * Action verb:      `completed` (added downstream by Juno-registered schema)
 * Org ID type:      `tenant_id` (site_luid)
 *
 * Emitted after the content space provider has finished loading the workbook
 * or datasource detail AND the content's project_id matches the Admin Insights
 * project. This means the event fires on successful open only (not on 403s,
 * load failures, or non-Admin-Insights content).
 *
 * PII discipline: we do NOT log user names, emails, or free-text. Only
 * structural identifiers (LUIDs, project IDs) and categorical descriptors
 * (content name, action type).
 *
 * Juno PFT ID: aJCEE0000001gpm4AA
 */

// --- Workbook event ---

export const ADMIN_INSIGHTS_WORKBOOK_EVENT_TYPE = 'admin_insights_workbook';

export type AdminInsightsAction =
  | 'view'
  | 'publish'
  | 'refresh'
  | 'download'
  | 'changeOwner'
  | 'delete'
  | 'editSourcePublish'
  | 'share'
  | 'move';

export interface AdminInsightsWorkbookTelemetryEvent extends ValidTelemetryEvent {
  /** Wire event name. Always `admin_insights_workbook`. */
  type: typeof ADMIN_INSIGHTS_WORKBOOK_EVENT_TYPE;
  /**
   * PFT Standard Attribute for tenant routing. On Tableau Cloud the tenant
   * is the site, so this equals site_luid. Set per-event so non-Admin-Insights
   * events (owned by other teams) don't inherit a Standard Attribute their
   * schemas don't declare.
   */
  tenant_id: string;
  /** The Admin Insights project ID (as string). */
  project_id: string;
  /** The workbook LUID. */
  content_luid: string;
  /** The workbook display name (e.g., "TS Events", "TS Users"). */
  content_name: string;
  /** The user action: view, publish, refresh, download, changeOwner, delete, editSourcePublish, share, or move. */
  action: AdminInsightsAction;
}

// --- Datasource event ---

export const ADMIN_INSIGHTS_DATASOURCE_EVENT_TYPE = 'admin_insights_datasource';

export interface AdminInsightsDatasourceTelemetryEvent extends ValidTelemetryEvent {
  /** Wire event name. Always `admin_insights_datasource`. */
  type: typeof ADMIN_INSIGHTS_DATASOURCE_EVENT_TYPE;
  /** PFT Standard Attribute for tenant routing (site_luid). */
  tenant_id: string;
  /** The Admin Insights project ID (as string). */
  project_id: string;
  /** The datasource LUID. */
  content_luid: string;
  /** The datasource display name (e.g., "Site Content", "TS Events"). */
  content_name: string;
  /** The user action: view, publish, refresh, download, changeOwner, delete, editSourcePublish, share, or move. */
  action: AdminInsightsAction;
}

// --- Flow event ---

export const ADMIN_INSIGHTS_FLOW_EVENT_TYPE = 'admin_insights_flow';

export interface AdminInsightsFlowTelemetryEvent extends ValidTelemetryEvent {
  /** Wire event name. Always `admin_insights_flow`. */
  type: typeof ADMIN_INSIGHTS_FLOW_EVENT_TYPE;
  /** PFT Standard Attribute for tenant routing (site_luid). */
  tenant_id: string;
  /** The Admin Insights project ID (as string). */
  project_id: string;
  /** The flow LUID. */
  content_luid: string;
  /** The flow display name. */
  content_name: string;
  /** The user action: typically `publish` for flows. */
  action: AdminInsightsAction;
}

// --- Send helpers ---

/**
 * Emit `admin_insights_workbook` PFT event. The `.completed` action verb is
 * registered on the Juno schema and applied by Endeavour downstream -- the
 * wire `type` field is the bare event name.
 */
export function sendAdminInsightsWorkbookTelemetry(params: {
  readonly projectId: string;
  readonly contentLuid: string;
  readonly contentName: string;
  readonly action: AdminInsightsAction;
}): void {
  Telemetry.sendEvent<AdminInsightsWorkbookTelemetryEvent>({
    type: ADMIN_INSIGHTS_WORKBOOK_EVENT_TYPE,
    tenant_id: getSessionManagementServiceInstance().sessionSiteLuid(),
    project_id: params.projectId,
    content_luid: params.contentLuid,
    content_name: params.contentName,
    action: params.action,
  });
}

/**
 * Emit `admin_insights_datasource` PFT event. Same downstream enrichment as
 * the workbook event.
 */
export function sendAdminInsightsDatasourceTelemetry(params: {
  readonly projectId: string;
  readonly contentLuid: string;
  readonly contentName: string;
  readonly action: AdminInsightsAction;
}): void {
  Telemetry.sendEvent<AdminInsightsDatasourceTelemetryEvent>({
    type: ADMIN_INSIGHTS_DATASOURCE_EVENT_TYPE,
    tenant_id: getSessionManagementServiceInstance().sessionSiteLuid(),
    project_id: params.projectId,
    content_luid: params.contentLuid,
    content_name: params.contentName,
    action: params.action,
  });
}

/**
 * Emit `admin_insights_flow` PFT event. Same downstream enrichment as the
 * workbook and datasource events. Fired when a Prep flow that lives in the
 * Admin Insights project is published.
 */
export function sendAdminInsightsFlowTelemetry(params: {
  readonly projectId: string;
  readonly contentLuid: string;
  readonly contentName: string;
  readonly action: AdminInsightsAction;
}): void {
  Telemetry.sendEvent<AdminInsightsFlowTelemetryEvent>({
    type: ADMIN_INSIGHTS_FLOW_EVENT_TYPE,
    tenant_id: getSessionManagementServiceInstance().sessionSiteLuid(),
    project_id: params.projectId,
    content_luid: params.contentLuid,
    content_name: params.contentName,
    action: params.action,
  });
}
